import { useRef, useState, type CSSProperties } from "react";
import { X } from "lucide-react";
import { Button } from "@satyrsmc/shared/components";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { trpc } from "@/trpc";
import { clickToFaceBox, drawnImageRect, type NormalizedBox } from "./faceTagGeometry";

/** One tag as the editor holds it: who, and where their face box sits. */
export interface QueuedPhotoTag extends NormalizedBox {
  contact_id: string;
  display_name: string;
}

interface PhotoTagEditorProps {
  eventId: string;
  photoName: string;
  /** The image shown large — the upload wizard passes the server-signed web preview. */
  previewUrl: string;
  tags: QueuedPhotoTag[];
  onAddTag: (tag: QueuedPhotoTag) => void;
  onRemoveTag: (contactId: string) => void;
  onClose: () => void;
}

function boxStyle(box: NormalizedBox): CSSProperties {
  return {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };
}

/**
 * Tag mode for one photo: the preview shown large, with face boxes dropped by
 * clicking and named via a contact autocomplete. The editor owns no tag state
 * — the parent supplies `tags` and reacts to onAddTag/onRemoveTag, so the
 * upload wizard can persist each edit through its own mutations immediately.
 */
export function PhotoTagEditor({
  eventId,
  photoName,
  previewUrl,
  tags,
  onAddTag,
  onRemoveTag,
  onClose,
}: PhotoTagEditorProps) {
  const [pendingBox, setPendingBox] = useState<NormalizedBox | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const search = trpc.members.gallery.searchTagNames.useQuery(
    { event_id: eventId, q: debouncedQuery, limit: 20 },
    { enabled: pendingBox !== null && debouncedQuery.trim().length > 0 },
  );
  // The same person can't be boxed twice on one photo, so anyone already
  // tagged disappears from the options rather than erroring on select.
  const taggedIds = new Set(tags.map((tag) => tag.contact_id));
  const options = (search.data ?? []).filter((option) => !taggedIds.has(option.id));

  const handleQueryChange = (next: string) => {
    setQuery(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(next), 300);
  };

  const cancelPending = () => {
    setPendingBox(null);
    setQuery("");
    setDebouncedQuery("");
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    const img = imgRef.current;
    if (!img) return;
    // Coordinates are normalized against the image as drawn, not the element:
    // an object-fit preview can letterbox, and the stored box must land on the
    // same face whatever size the photo is later served at.
    const drawn = drawnImageRect(img.getBoundingClientRect(), img.naturalWidth, img.naturalHeight);
    const box = clickToFaceBox(event.clientX, event.clientY, drawn);
    if (!box) return;
    setPendingBox(box);
    setQuery("");
    setDebouncedQuery("");
  };

  const handleSelect = (option: { id: string; display_name: string }) => {
    if (!pendingBox) return;
    onAddTag({ ...pendingBox, contact_id: option.id, display_name: option.display_name });
    cancelPending();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="h-[90dvh] max-h-none w-screen max-w-none sm:max-w-4xl gap-0 overflow-hidden p-0"
        onEscapeKeyDown={(event) => {
          // Escape backs out of the in-progress box first; only a second
          // Escape (with nothing pending) closes the editor.
          if (pendingBox) {
            event.preventDefault();
            cancelPending();
          }
        }}
      >
        <DialogTitle className="sr-only">Tag people in {photoName}</DialogTitle>
        <div className="flex h-full min-h-0 flex-col md:flex-row">
          <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center bg-black/95 p-4">
            <div className="relative inline-block">
              <img
                ref={imgRef}
                src={previewUrl}
                alt={photoName}
                className="block max-h-[70vh] max-w-full cursor-crosshair"
                onClick={handleImageClick}
              />

              {tags.map((tag) => (
                <div
                  key={tag.contact_id}
                  className="pointer-events-none absolute border-2 border-yellow-400"
                  style={boxStyle(tag)}
                >
                  <span className="absolute top-full left-1/2 mt-1 -translate-x-1/2 rounded bg-black/70 px-1.5 py-0.5 text-xs whitespace-nowrap text-white">
                    {tag.display_name}
                  </span>
                </div>
              ))}

              {pendingBox && (
                <>
                  <div
                    className="pointer-events-none absolute border-2 border-dashed border-yellow-400"
                    style={boxStyle(pendingBox)}
                  />
                  <div
                    className="absolute z-10 w-64 -translate-x-1/2 space-y-1 rounded-md border border-border bg-background p-2 shadow-lg"
                    style={{
                      left: `${(pendingBox.x + pendingBox.w / 2) * 100}%`,
                      top: `${(pendingBox.y + pendingBox.h) * 100}%`,
                    }}
                  >
                    <input
                      autoFocus
                      type="text"
                      aria-label="Search people to tag"
                      placeholder="Type a name…"
                      value={query}
                      onChange={(event) => handleQueryChange(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          event.stopPropagation();
                          cancelPending();
                        }
                      }}
                      className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
                    />
                    <div className="max-h-40 overflow-y-auto">
                      {search.isFetching && (
                        <p className="px-2 py-1 text-xs text-muted-foreground">Searching…</p>
                      )}
                      {options.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                          onClick={() => handleSelect(option)}
                        >
                          {option.display_name}
                        </button>
                      ))}
                      {debouncedQuery.trim() && !search.isFetching && options.length === 0 && (
                        <p className="px-2 py-1 text-xs text-muted-foreground">No matches</p>
                      )}
                    </div>
                    <Button size="sm" variant="ghost" className="w-full" onClick={cancelPending}>
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          <aside className="w-full shrink-0 space-y-4 overflow-y-auto border-t border-border p-4 text-sm md:w-72 md:border-t-0 md:border-l">
            <p className="truncate font-medium">{photoName}</p>

            <p className="text-muted-foreground">Click a face in the photo, then pick a name.</p>

            <div className="space-y-1">
              <p className="text-xs tracking-wide text-muted-foreground uppercase">Tagged</p>
              {tags.length === 0 ? (
                <p className="text-muted-foreground">No one tagged yet.</p>
              ) : (
                <ul className="space-y-1">
                  {tags.map((tag) => (
                    <li
                      key={tag.contact_id}
                      className="flex items-center justify-between gap-2 rounded border border-border px-2 py-1"
                    >
                      <span className="truncate">{tag.display_name}</span>
                      <button
                        type="button"
                        aria-label={`Remove tag for ${tag.display_name}`}
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => onRemoveTag(tag.contact_id)}
                      >
                        <X className="size-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Button size="sm" className="w-full" onClick={onClose}>
              Done
            </Button>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
